import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Target,
  Award
} from 'lucide-react';
import ActivityMetrics from './ActivityMetrics';

// Simple counter - no animation to avoid bugs
const SimpleCounter = ({ value }) => {
  const displayValue = typeof value === 'number' ? value : parseInt(value) || 0;
  return <span>{displayValue.toLocaleString()}</span>;
};

// Mini sparkline chart - simplified
const Sparkline = ({ data, color = 'blue', height = 32 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const getColorClass = () => {
    switch(color) {
      case 'green': return '#22c55e';
      case 'amber': return '#f59e0b';
      case 'red': return '#ef4444';
      default: return '#3b82f6';
    }
  };
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full h-8" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={getColorClass()}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        opacity="0.8"
      />
    </svg>
  );
};

// Circular progress chart
const CircularProgress = ({ value, max = 100, size = 120, strokeWidth = 10, color = 'blue' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percent = (value / max) * 100;
  const offset = circumference - (percent / 100) * circumference;

  const colors = {
    blue: '#3b82f6',
    green: '#22c55e',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#8b5cf6',
    cyan: '#06b6d4'
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-slate-700"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colors[color]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-white">{Math.round(percent)}%</span>
      </div>
    </div>
  );
};

// Bar chart component
const BarChart = ({ data, labels, color = 'blue' }) => {
  const max = Math.max(...data);

  const colors = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className="flex items-end justify-between h-32 gap-2">
      {data.map((value, index) => (
        <div key={index} className="flex flex-col items-center flex-1">
          <motion.div
            className={`w-full ${colors[color]} rounded-t-md`}
            initial={{ height: 0 }}
            animate={{ height: `${(value / max) * 100}%` }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          />
          <span className="text-xs text-slate-500 mt-2">{labels[index]}</span>
        </div>
      ))}
    </div>
  );
};

const EnhancedDashboardCharts = ({ stats, contracts = [] }) => {
  // Calculate statistics
  const totalContracts = stats?.total_contracts || contracts.length || 0;
  const signedContracts = stats?.signed_contracts || contracts.filter(c => c.is_signed).length || 0;
  const draftContracts = stats?.draft_contracts || contracts.filter(c => c.status === 'borrador').length || 0;
  const averageRisk = stats?.average_risk || 45;

  // Mock data for charts (would come from real analytics in production)
  const weeklyData = [3, 5, 2, 8, 6, 4, 7];
  const weekLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  
  const monthlyTrend = [12, 19, 15, 25, 22, 30, 28, 35, 32, 40, 38, 45];
  
  const contractTypes = [
    { name: 'Compraventa', value: 35, color: 'blue' },
    { name: 'Arriendo', value: 25, color: 'green' },
    { name: 'Laboral', value: 20, color: 'amber' },
    { name: 'Otros', value: 20, color: 'purple' }
  ];

  const statCards = [
    {
      title: 'Total Contratos',
      value: totalContracts,
      icon: FileText,
      color: 'blue',
      trend: '+12%',
      trendUp: true,
      sparkData: [2, 4, 3, 5, 4, 6, 7]
    },
    {
      title: 'Firmados',
      value: signedContracts,
      icon: CheckCircle,
      color: 'green',
      trend: '+8%',
      trendUp: true,
      sparkData: [1, 2, 2, 3, 3, 4, 4]
    },
    {
      title: 'Borradores',
      value: draftContracts,
      icon: Clock,
      color: 'amber',
      trend: '-5%',
      trendUp: false,
      sparkData: [4, 3, 4, 3, 2, 3, 3]
    },
    {
      title: 'Riesgo Promedio',
      value: averageRisk,
      icon: AlertTriangle,
      color: averageRisk > 60 ? 'red' : averageRisk > 40 ? 'amber' : 'green',
      trend: '-3%',
      trendUp: true,
      sparkData: [60, 55, 50, 48, 45, 43, 45]
    }
  ];

  return (
    <div className="space-y-4 pb-24">
      {/* Stats Cards - Simplified for mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-slate-800/50 rounded-xl p-3 sm:p-4 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-400 text-xs sm:text-sm truncate">{stat.title}</p>
              <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                stat.color === 'blue' ? 'bg-blue-500/20' :
                stat.color === 'green' ? 'bg-green-500/20' :
                stat.color === 'amber' ? 'bg-amber-500/20' : 'bg-red-500/20'
              }`}>
                <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  stat.color === 'blue' ? 'text-blue-400' :
                  stat.color === 'green' ? 'text-green-400' :
                  stat.color === 'amber' ? 'text-amber-400' : 'text-red-400'
                }`} />
              </div>
            </div>
            
            <p className="text-xl sm:text-2xl font-bold text-white mb-2">
              <SimpleCounter value={stat.value} />
              {stat.title === 'Riesgo Promedio' && '%'}
            </p>
            
            {/* Sparkline - hidden on very small screens */}
            <div className="h-8 mb-2 hidden sm:block">
              <Sparkline data={stat.sparkData} color={stat.color} />
            </div>
            
            {/* Trend */}
            <div className={`flex items-center text-xs ${stat.trendUp ? 'text-green-400' : 'text-red-400'}`}>
              {stat.trendUp ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
              <span className="truncate">{stat.trend} vs anterior</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row - Stack on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Weekly Activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              <h3 className="text-white text-sm font-medium">Actividad Semanal</h3>
            </div>
            <span className="text-xs text-slate-500">Esta semana</span>
          </div>
          <div className="h-32">
            <BarChart data={weeklyData} labels={weekLabels} color="blue" />
          </div>
        </motion.div>

        {/* Contract Completion Rate */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-green-400" />
              <h3 className="text-white text-sm font-medium">Tasa de Completado</h3>
            </div>
          </div>
          <div className="flex items-center justify-center py-2">
            <CircularProgress 
              value={totalContracts > 0 ? signedContracts : 0} 
              max={totalContracts || 1} 
              size={100}
              strokeWidth={8}
              color="green" 
            />
          </div>
          <p className="text-center text-slate-400 text-xs mt-2">
            {signedContracts} de {totalContracts} firmados
          </p>
        </motion.div>

        {/* Contract Types Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <PieChart className="w-4 h-4 text-purple-400" />
              <h3 className="text-white text-sm font-medium">Tipos de Contrato</h3>
            </div>
          </div>
          <div className="space-y-2">
            {contractTypes.map((type, index) => (
              <div key={type.name} className="flex items-center">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-300 truncate">{type.name}</span>
                    <span className="text-xs text-slate-400">{type.value}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full ${
                        type.color === 'blue' ? 'bg-blue-500' :
                        type.color === 'green' ? 'bg-green-500' :
                        type.color === 'amber' ? 'bg-amber-500' : 'bg-purple-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${type.value}%` }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Monthly Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-slate-800/50 rounded-xl p-5 border border-slate-700"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            <h3 className="text-white font-medium">Tendencia Anual</h3>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <span className="flex items-center text-green-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              +25% crecimiento
            </span>
          </div>
        </div>
        <div className="h-20">
          <Sparkline data={monthlyTrend} color="cyan" height={80} />
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-500">
          <span>Ene</span>
          <span>Feb</span>
          <span>Mar</span>
          <span>Abr</span>
          <span>May</span>
          <span>Jun</span>
          <span>Jul</span>
          <span>Ago</span>
          <span>Sep</span>
          <span>Oct</span>
          <span>Nov</span>
          <span>Dic</span>
        </div>
      </motion.div>

      {/* Activity Metrics Section */}
      <div className="mt-6">
        <ActivityMetrics showTitle={true} />
      </div>
    </div>
  );
};

export default EnhancedDashboardCharts;
